import { useState } from 'react';
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, ChevronUp, Copy, Eye, GripVertical, Plus, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { FormField, FormFieldType } from '@/lib/api';
import { FormRenderer } from './FormRenderer';
import { FIELD_TYPE_LABELS, FIELD_TYPE_ORDER, newFieldId } from './form-utils';

interface FormBuilderProps {
  fields: FormField[];
  onChange: (fields: FormField[]) => void;
  disabled?: boolean;
}

const CHOICE_TYPES: FormFieldType[] = ['single_choice', 'multiple_choice'];

function blankField(type: FormFieldType): FormField {
  const base: FormField = { id: newFieldId(), type, label: '' };
  if (CHOICE_TYPES.includes(type)) base.options = ['Option 1', 'Option 2'];
  if (type === 'file') base.maxFiles = 1;
  return base;
}

/**
 * The field list: add, reorder, edit, remove. Keeps no server state of its own —
 * the page owns `fields` and saves.
 */
export function FormBuilder({ fields, onChange, disabled }: FormBuilderProps) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [preview, setPreview] = useState(false);
  const [previewAnswers, setPreviewAnswers] = useState<Record<string, unknown>>({});

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const update = (id: string, patch: Partial<FormField>) => onChange(fields.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  const remove = (id: string) => {
    onChange(fields.filter((f) => f.id !== id));
    if (expanded === id) setExpanded(null);
  };
  const duplicate = (id: string) => {
    const i = fields.findIndex((f) => f.id === id);
    if (i < 0) return;
    const copy = { ...fields[i], id: newFieldId(), label: fields[i].label ? `${fields[i].label} (copy)` : '' };
    const next = [...fields];
    next.splice(i + 1, 0, copy);
    onChange(next);
    setExpanded(copy.id);
  };
  const add = (type: FormFieldType) => {
    const f = blankField(type);
    onChange([...fields, f]);
    setExpanded(f.id);
    setPreview(false);
  };
  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = fields.findIndex((f) => f.id === active.id);
    const to = fields.findIndex((f) => f.id === over.id);
    onChange(arrayMove(fields, from, to));
  };

  const questionCount = fields.filter((f) => f.type !== 'section').length;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <div className="lg:col-span-8 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm text-grey-500">
            {questionCount} question{questionCount === 1 ? '' : 's'}
            {fields.length !== questionCount ? ` · ${fields.length - questionCount} section${fields.length - questionCount === 1 ? '' : 's'}` : ''}
          </p>
          <button
            type="button"
            onClick={() => setPreview((p) => !p)}
            className={cn('inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border transition-colors', preview ? 'border-accent text-accent bg-accent/5' : 'border-grey-200 text-grey-600 hover:bg-grey-50')}
            aria-pressed={preview}
          >
            <Eye className="w-4 h-4" /> {preview ? 'Back to editing' : 'Preview'}
          </button>
        </div>

        {preview ? (
          <div className="card p-6">
            {fields.length === 0 ? (
              <p className="text-sm text-grey-500">Add a question to see the preview.</p>
            ) : (
              <FormRenderer fields={fields} answers={previewAnswers} onChange={(id, v) => setPreviewAnswers((a) => ({ ...a, [id]: v }))} />
            )}
          </div>
        ) : fields.length === 0 ? (
          <div className="card p-8 text-center border-dashed">
            <p className="text-sm font-medium text-secondary">No questions yet</p>
            <p className="text-sm text-grey-500 mt-1">Pick a question type on the right to get started.</p>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={fields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {fields.map((f, i) => (
                  <FieldCard
                    key={f.id}
                    field={f}
                    index={i}
                    expanded={expanded === f.id}
                    onToggle={() => setExpanded(expanded === f.id ? null : f.id)}
                    onChange={(patch) => update(f.id, patch)}
                    onRemove={() => remove(f.id)}
                    onDuplicate={() => duplicate(f.id)}
                    disabled={disabled}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      <aside className="lg:col-span-4">
        <div className="card p-4 lg:sticky lg:top-20">
          <p className="text-xs font-semibold uppercase tracking-wide text-grey-400 mb-3">Add a question</p>
          <div className="grid grid-cols-2 gap-2">
            {FIELD_TYPE_ORDER.map((t) => (
              <button
                key={t}
                type="button"
                disabled={disabled}
                onClick={() => add(t)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-grey-200 text-sm text-secondary hover:border-accent hover:text-accent transition-colors text-left disabled:opacity-50"
              >
                <Plus className="w-3.5 h-3.5 shrink-0 text-grey-400" />
                <span className="truncate">{FIELD_TYPE_LABELS[t]}</span>
              </button>
            ))}
          </div>
          <p className="text-xs text-grey-500 mt-4">Drag the handle to reorder. Each question's id is fixed, so renaming a question keeps its answers.</p>
        </div>
      </aside>
    </div>
  );
}

function FieldCard({
  field,
  index,
  expanded,
  onToggle,
  onChange,
  onRemove,
  onDuplicate,
  disabled,
}: {
  field: FormField;
  index: number;
  expanded: boolean;
  onToggle: () => void;
  onChange: (patch: Partial<FormField>) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  disabled?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: field.id, disabled });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const isSection = field.type === 'section';
  const isChoice = CHOICE_TYPES.includes(field.type);
  const labelId = `label-${field.id}`;

  return (
    <div ref={setNodeRef} style={style} className={cn('bg-white rounded-lg border transition-all', isDragging ? 'opacity-50 shadow-lg border-accent' : 'border-grey-100', isSection && 'bg-grey-50/60')}>
      <div className="flex items-center gap-2 px-2 py-2">
        <button type="button" {...attributes} {...listeners} className="p-1 text-grey-400 hover:text-grey-600 cursor-grab active:cursor-grabbing touch-none" aria-label="Drag to reorder" disabled={disabled}>
          <GripVertical className="w-4 h-4" />
        </button>
        <button type="button" onClick={onToggle} className="flex-1 min-w-0 flex items-center gap-3 text-left py-1" aria-expanded={expanded}>
          <span className="text-xs text-grey-400 tabular-nums w-5 shrink-0">{index + 1}.</span>
          <span className={cn('flex-1 min-w-0 truncate text-sm', field.label ? 'text-secondary font-medium' : 'text-grey-400 italic')}>{field.label || `Untitled ${FIELD_TYPE_LABELS[field.type].toLowerCase()}`}</span>
          <span className="text-xs text-grey-400 shrink-0 hidden sm:inline">{FIELD_TYPE_LABELS[field.type]}</span>
          {field.required && <span className="text-xs text-error shrink-0">Required</span>}
          {expanded ? <ChevronUp className="w-4 h-4 text-grey-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-grey-400 shrink-0" />}
        </button>
        <button type="button" onClick={onDuplicate} disabled={disabled} className="p-1.5 text-grey-400 hover:text-secondary rounded transition-colors" aria-label="Duplicate question">
          <Copy className="w-4 h-4" />
        </button>
        <button type="button" onClick={onRemove} disabled={disabled} className="p-1.5 text-grey-400 hover:text-error rounded transition-colors" aria-label="Remove question">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-grey-100 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <label htmlFor={labelId} className="block text-xs font-medium text-grey-600 mb-1">
                {isSection ? 'Heading' : 'Question'}
              </label>
              <input id={labelId} type="text" value={field.label} onChange={(e) => onChange({ label: e.target.value })} className="input w-full" placeholder={isSection ? 'About your business' : 'What should we call your project?'} autoFocus disabled={disabled} />
            </div>
            <div>
              <label htmlFor={`type-${field.id}`} className="block text-xs font-medium text-grey-600 mb-1">
                Type
              </label>
              <select
                id={`type-${field.id}`}
                value={field.type}
                disabled={disabled}
                onChange={(e) => {
                  const type = e.target.value as FormFieldType;
                  const patch: Partial<FormField> = { type };
                  if (CHOICE_TYPES.includes(type) && !field.options?.length) patch.options = ['Option 1', 'Option 2'];
                  if (!CHOICE_TYPES.includes(type)) patch.options = undefined;
                  if (type === 'section') patch.required = undefined;
                  if (type === 'file' && !field.maxFiles) patch.maxFiles = 1;
                  onChange(patch);
                }}
                className="input w-full"
              >
                {FIELD_TYPE_ORDER.map((t) => (
                  <option key={t} value={t}>
                    {FIELD_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor={`help-${field.id}`} className="block text-xs font-medium text-grey-600 mb-1">
              {isSection ? 'Instructions (optional)' : 'Help text (optional)'}
            </label>
            <textarea id={`help-${field.id}`} rows={2} value={field.helpText ?? ''} onChange={(e) => onChange({ helpText: e.target.value || undefined })} className="input w-full" placeholder="Shown under the question." disabled={disabled} />
          </div>

          {isChoice && (
            <div>
              <label htmlFor={`options-${field.id}`} className="block text-xs font-medium text-grey-600 mb-1">
                Options — one per line
              </label>
              <OptionsEditor id={`options-${field.id}`} options={field.options ?? []} onChange={(options) => onChange({ options })} disabled={disabled} />
            </div>
          )}

          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            {!isSection && (
              <label className="inline-flex items-center gap-2 text-sm text-secondary cursor-pointer">
                <input type="checkbox" checked={!!field.required} onChange={(e) => onChange({ required: e.target.checked })} className="w-4 h-4 rounded text-accent border-grey-300 focus:ring-accent" disabled={disabled} />
                Required
              </label>
            )}
            {field.type === 'file' && (
              <label className="inline-flex items-center gap-2 text-sm text-secondary">
                Max files
                <input type="number" min={1} max={20} value={field.maxFiles ?? 1} onChange={(e) => onChange({ maxFiles: Math.max(1, Math.min(20, Number(e.target.value) || 1)) })} className="input w-20 py-1.5 px-2" disabled={disabled} />
              </label>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** A textarea that round-trips "one option per line" without eating the user's trailing newline while typing. */
function OptionsEditor({ id, options, onChange, disabled }: { id: string; options: string[]; onChange: (o: string[]) => void; disabled?: boolean }) {
  const [text, setText] = useState(options.join('\n'));
  return (
    <textarea
      id={id}
      rows={Math.min(8, Math.max(3, options.length + 1))}
      value={text}
      disabled={disabled}
      onChange={(e) => {
        setText(e.target.value);
        onChange(e.target.value.split('\n').map((s) => s.trim()).filter(Boolean));
      }}
      onBlur={() => setText(options.join('\n'))}
      className="input w-full font-mono text-sm"
      placeholder={'Option 1\nOption 2'}
    />
  );
}
